// Pure translations from the IVS Chat Messaging SDK's objects to the frames the chat panel's
// received() switch has always consumed, so the panel's DOM code did not have to change shape
// with the transport. Nothing here touches the DOM.

// IVS delivers every sender attribute as a string (LiveChat::SenderAttributes): a flag is "true"
// or absent, never "false".
const SENDER_STRING_FIELDS = ["name", "initials", "avatar_url", "avatar_color_class"];
const SENDER_FLAG_FIELDS = ["is_channel_admin", "is_moderator", "has_subscriber_profile"];

// A chunked event whose remaining pieces have not arrived in this long is dropped (a chunk was
// lost, or we joined mid-sequence); IVS delivers the pieces within milliseconds of each other.
const CHUNK_TTL_MS = 30000;

export function parseSender(sender) {
  const attributes = (sender && sender.attributes) || {};
  const rawId = sender ? sender.userId : null;
  const numericId = Number(rawId);
  const user = { id: rawId != null && !Number.isNaN(numericId) ? numericId : rawId, name: "" };

  SENDER_STRING_FIELDS.forEach((field) => {
    if (typeof attributes[field] === "string") user[field] = attributes[field];
  });
  SENDER_FLAG_FIELDS.forEach((field) => {
    user[field] = attributes[field] === "true";
  });

  return user;
}

// A room message. client_msg_id is the sender's stable id (set in the message attributes so a
// retry can be deduped); it falls back to the IVS id for a message sent without one.
export function normalizeMessage(chatMessage) {
  const attributes = chatMessage.attributes || {};
  return {
    type: "message",
    id: chatMessage.id,
    client_msg_id: attributes.client_msg_id || chatMessage.id,
    user: parseSender(chatMessage.sender),
    body: chatMessage.content,
    at: chatMessage.sendTime.toISOString(),
  };
}

// Reassembles a payload LiveChat::RoomEvents split across several events because IVS caps an
// event's attributes at 1 KB: each piece carries chunk="<id>:<index>:<total>" and its slice of
// the JSON in payload. Pieces may arrive in any order.
export class ChunkAssembler {
  constructor(now = () => Date.now()) {
    this.now = now;
    this.pending = new Map();
  }

  // The complete JSON string once the last piece lands, null until then (or for a bad piece).
  add(chatEvent) {
    const attributes = chatEvent.attributes || {};
    const [id, indexText, totalText] = String(attributes.chunk).split(":");
    const index = Number(indexText);
    const total = Number(totalText);
    if (!id || !Number.isInteger(index) || !Number.isInteger(total) || total < 1 || index < 0 || index >= total) return null;

    this.evict();
    let entry = this.pending.get(id);
    if (!entry || entry.total !== total) {
      entry = { total, parts: new Map(), at: this.now() };
      this.pending.set(id, entry);
    }
    entry.parts.set(index, String(attributes.payload || ""));
    if (entry.parts.size < total) return null;

    this.pending.delete(id);
    return Array.from({ length: total }, (_, i) => entry.parts.get(i)).join("");
  }

  evict() {
    const cutoff = this.now() - CHUNK_TTL_MS;
    this.pending.forEach((entry, id) => {
      if (entry.at < cutoff) this.pending.delete(id);
    });
  }
}

// A server-published room event (LiveChat::RoomEvents): the event name is the frame type and the
// JSON `payload` attribute is spread over it. null when the payload is not (yet) a complete JSON
// object: a piece of a chunked event still waiting for its siblings, or malformed JSON.
export function normalizeEvent(chatEvent, assembler = null) {
  const attributes = chatEvent.attributes || {};
  let json = attributes.payload || "{}";
  if (attributes.chunk != null) {
    if (!assembler) return null;
    json = assembler.add(chatEvent);
    if (json === null) return null;
  }

  let payload;
  try {
    payload = JSON.parse(json);
  } catch (error) {
    return null;
  }
  if (payload === null || typeof payload !== "object" || Array.isArray(payload)) return null;

  return { type: chatEvent.eventName, ...payload };
}

export function normalizeDelete(deleteEvent) {
  return { type: "remove", message_id: deleteEvent.messageId };
}

// The ChatToken the SDK expects, from a session response. The SDK opens its replacement
// connection 60 s before tokenExpirationTime, so that field carries the server's jittered
// refresh_at (about one handover per session) - never AWS's raw one-minute token expiry, which
// would re-mint a token per viewer every ~30 s.
export function sessionToToken(bootstrap) {
  return {
    token: bootstrap.token,
    sessionExpirationTime: new Date(bootstrap.session_expiration_time),
    tokenExpirationTime: new Date(bootstrap.refresh_at),
  };
}
